import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  BarChart3,
  Loader2,
  Filter,
  History,
  Edit,
  Building2,
  Search,
  RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEAD_TYPE_COLORS = {
  cold_call: { bg: 'bg-blue-100', text: 'text-blue-800', badge: 'COLD' },
  live_transfer: { bg: 'bg-green-100', text: 'text-green-800', badge: 'LIVE' },
  data_lead: { bg: 'bg-purple-100', text: 'text-purple-800', badge: 'DATA' },
  robots: { bg: 'bg-cyan-100', text: 'text-cyan-800', badge: 'ROBO' },
  business_leads: { bg: 'bg-amber-100', text: 'text-amber-800', badge: 'BIZ' }
};

const BID_TYPE_COLORS = {
  shared: { bg: 'bg-orange-100', text: 'text-orange-800' },
  exclusive: { bg: 'bg-teal-100', text: 'text-teal-800' }
};

export default function AdminCampaignsPage() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  const [leadTypes, setLeadTypes] = useState([]);
  
  // Filters
  const [filterClientId, setFilterClientId] = useState(clientId || '');
  const [filterLeadType, setFilterLeadType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const getAuthHeaders = () => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [campaignsRes, clientsRes, leadTypesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/campaigns`, {
          headers: getAuthHeaders(),
          params: { client_id: clientId || undefined, limit: 500 }
        }),
        axios.get(`${API_URL}/api/admin/clients`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/campaigns/reference/lead-types`)
      ]);
      setCampaigns(campaignsRes.data);
      setClients(clientsRes.data);
      setLeadTypes(leadTypesRes.data);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const applyFilters = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (filterClientId) params.client_id = filterClientId;
      if (filterLeadType) params.lead_type = filterLeadType;
      if (filterStatus) params.is_active = filterStatus === 'active';
      
      const response = await axios.get(`${API_URL}/api/admin/campaigns`, {
        headers: getAuthHeaders(),
        params
      });
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to filter campaigns');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterClientId('');
    setFilterLeadType('');
    setFilterStatus('');
    fetchData();
  };

  const getLeadTypeStyle = (leadType) => {
    return LEAD_TYPE_COLORS[leadType] || { bg: 'bg-gray-100', text: 'text-gray-800', badge: leadType };
  };

  const getBidTypeStyle = (bidType) => {
    return BID_TYPE_COLORS[bidType] || { bg: 'bg-gray-100', text: 'text-gray-800' };
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-campaigns-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6" />
            Client Campaigns
          </h1>
          <p className="text-muted-foreground">View and manage all client campaigns</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/admin/campaigns/audit-logs')}>
            <History className="w-4 h-4 mr-2" />
            View All Audit Logs
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label className="text-sm">Client</Label>
              <Select value={filterClientId || "all"} onValueChange={(v) => setFilterClientId(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-sm">Lead Type</Label>
              <Select value={filterLeadType || "all"} onValueChange={(v) => setFilterLeadType(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {leadTypes.map(lt => (
                    <SelectItem key={lt.value} value={lt.value}>{lt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-sm">Status</Label>
              <Select value={filterStatus || "all"} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={applyFilters}>
              <Search className="w-4 h-4 mr-2" />
              Apply
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Campaigns</div>
            <div className="text-2xl font-bold">{campaigns.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Active</div>
            <div className="text-2xl font-bold text-green-600">
              {campaigns.filter(c => c.is_active).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Leads</div>
            <div className="text-2xl font-bold">
              {campaigns.reduce((sum, c) => sum + (c.stats?.leads || 0), 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-muted-foreground">Total Spend</div>
            <div className="text-2xl font-bold">
              {formatCurrency(campaigns.reduce((sum, c) => sum + (c.stats?.spend || 0), 0))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-800 text-white hover:bg-slate-800">
                <TableHead className="text-white">Status</TableHead>
                <TableHead className="text-white">Client</TableHead>
                <TableHead className="text-white">Campaign</TableHead>
                <TableHead className="text-white">Type</TableHead>
                <TableHead className="text-white">Bid</TableHead>
                <TableHead className="text-white">States</TableHead>
                <TableHead className="text-white">Leads</TableHead>
                <TableHead className="text-white">Spend</TableHead>
                <TableHead className="text-white">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No campaigns found
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((campaign) => {
                  const leadStyle = getLeadTypeStyle(campaign.lead_type);
                  const bidStyle = getBidTypeStyle(campaign.bid_type);
                  return (
                    <TableRow key={campaign.id}>
                      <TableCell>
                        <Badge variant={campaign.is_active ? 'default' : 'secondary'}>
                          {campaign.is_active ? 'ON' : 'OFF'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{campaign.client_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div 
                          className="font-medium text-teal-600 hover:underline cursor-pointer"
                          onClick={() => navigate(`/admin/campaigns/${campaign.id}/edit`)}
                        >
                          {campaign.name}
                        </div>
                        {campaign.description && (
                          <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {campaign.description}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Badge className={`${leadStyle.bg} ${leadStyle.text} text-xs`}>
                            {leadStyle.badge}
                          </Badge>
                          <Badge className={`${bidStyle.bg} ${bidStyle.text} text-xs`}>
                            {campaign.bid_type?.toUpperCase()}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>{formatCurrency(campaign.max_bid)}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{campaign.states?.length || 0} states</Badge>
                      </TableCell>
                      <TableCell>{campaign.stats?.leads || 0}</TableCell>
                      <TableCell>{formatCurrency(campaign.stats?.spend || 0)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/admin/campaigns/${campaign.id}/edit`)}
                            title="Edit/Override"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => navigate(`/admin/campaigns/${campaign.id}/history`)}
                            title="View History"
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
